<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type { Project, ProjectStatus } from '../lib/types';
  import {
    draggedProject,
    dropTarget,
    openProjectModalForCreate,
    requestProjectMove,
  } from '../lib/stores/ui';
  import ProjectCard from './ProjectCard.svelte';

  interface Props {
    title: string;
    status: ProjectStatus;
    monthKey: string;
    projects: Project[];
    isFuture?: boolean;
  }

  let { title, status, monthKey, projects, isFuture = false }: Props = $props();

  const statusColors: Record<ProjectStatus, string> = {
    planned: 'bg-blue-50/70',
    active: 'bg-amber-50/70',
    almost: 'bg-orange-50/70',
    done: 'bg-green-50/70',
  };

  const statusIcons: Record<ProjectStatus, string> = {
    planned: '📋',
    active: '🔄',
    almost: '🏁',
    done: '✅',
  };

  // Use purple for future "Targets" section
  let bgColor = $derived(isFuture ? 'bg-purple-50/70' : statusColors[status]);
  let icon = $derived(isFuture ? '🎯' : statusIcons[status]);

  let isDropTarget = $state(false);
  let isHovering = $state(false);

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if ($draggedProject) {
      isDropTarget = true;
      dropTarget.set({ monthKey, status });
    }
  }

  function handleDragLeave(e: DragEvent) {
    // Only trigger if leaving the actual container, not moving to a child
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      isDropTarget = false;
      dropTarget.set(null);
    }
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDropTarget = false;
    dropTarget.set(null);

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'month', { monthKey, status });
    }
  }

  function handleAddProject() {
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey,
      status,
    });
  }
</script>

<div
  class="status-section {bgColor} transition-all"
  class:ring-2={isDropTarget}
  class:ring-blue-400={isDropTarget}
  class:bg-blue-100={isDropTarget}
  ondragover={handleDragOver}
  ondragleave={handleDragLeave}
  ondrop={handleDrop}
  onmouseenter={() => (isHovering = true)}
  onmouseleave={() => (isHovering = false)}
  role="list"
>
  <!-- Section header -->
  <div class="mb-1.5 flex items-center gap-1.5">
    <span class="text-sm">{icon}</span>
    <h4 class="text-xs font-semibold tracking-wide text-gray-600 uppercase">{title}</h4>
    <span class="rounded-full bg-gray-200/70 px-1 py-0.5 text-[10px] text-gray-400">
      {projects.length}
    </span>

    <!-- Add button (visible on hover) -->
    {#if !isFuture}
      <button
        class="ml-auto rounded p-0.5 text-gray-400 transition-all hover:bg-blue-100 hover:text-blue-600
               {isHovering ? 'opacity-100' : 'opacity-0'}"
        onclick={handleAddProject}
        title="Add project"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>
    {/if}
  </div>

  <!-- Cards grid - wraps properly -->
  <div class="cards-grid">
    {#each projects as project (project.id)}
      <ProjectCard {project} compact clickable />
    {/each}

    {#if projects.length === 0}
      <div class="empty-state">
        {#if isFuture}
          Drop targets here
        {:else}
          Drop projects or click + to add
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .status-section {
    padding: 0.5rem;
    border-radius: 0.375rem;
    min-height: 3.5rem;
    overflow: hidden;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.375rem;
  }

  /* Responsive: fewer columns on smaller screens */
  @media (max-width: 1400px) {
    .cards-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 1000px) {
    .cards-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .empty-state {
    grid-column: 1 / -1;
    text-align: center;
    color: #9ca3af;
    font-size: 0.75rem;
    padding: 1rem 0;
  }
</style>
